// Compare lens model and diffing utilities (FE-063).
// Aligns two evidence versions along shared structural anchors.
// Labels all differences explicitly with text and non-color glyphs.
// Supports accessible linear reading.

import type { Evidence, Finding, SourceRecord } from './types.ts';
import { collectorText, neutralPointer } from '../discovery/collectorLabel.ts';

export type AnchorCategory =
  | 'identity'
  | 'classification'
  | 'provenance'
  | 'integrity'
  | 'summary'
  | 'content'
  | 'citations'
  | 'transformations';

export type AnchorStatus = 'UNCHANGED' | 'MODIFIED' | 'ADDED' | 'REMOVED' | 'DRIFT';

export interface CompareAnchor {
  id: string;
  label: string;
  category: AnchorCategory;
  status: AnchorStatus;
  statusLabel: string;
  glyph: string;
  diffExplanation: string;
  baselineValue: string | null;
  targetValue: string | null;
  isMonospace?: boolean;
}

export interface EvidenceCompareItem {
  id: string;
  label?: string;
  kind: string;
  sourceClass: 'RAW_TOOL_OUTPUT' | 'MODEL_ASSISTED' | string;
  sourcePointer: string;
  sourceSystem?: string;
  toolVersion?: string;
  capturedAt: string;
  sha256?: string;
  storageRef?: string;
  integrityState?: string;
  summary?: string;
  content?: string;
  isAiInterpretation?: boolean;
  citingFindings?: Array<{
    findingId: string;
    title: string;
    severity?: string;
  }>;
  transformations?: Array<{
    transformationId: string;
    name: string;
    class: string;
    performedAt: string;
  }>;
}

export interface EvidenceComparisonSummary {
  totalAnchors: number;
  unchangedCount: number;
  modifiedCount: number;
  addedCount: number;
  removedCount: number;
  driftCount: number;
  hasDifferences: boolean;
}

export interface EvidenceComparisonResult {
  isReady: boolean;
  baseline: EvidenceCompareItem | null;
  target: EvidenceCompareItem | null;
  anchors: CompareAnchor[];
  summary: EvidenceComparisonSummary;
}

/**
 * Normalizes an Evidence record into an EvidenceCompareItem.
 */
export function evidenceToCompareItem(
  ev: Evidence,
  sourceRecord?: SourceRecord | null,
  allFindings?: readonly Finding[],
): EvidenceCompareItem {
  const citing = allFindings
    ? allFindings.filter((f) => f.evidenceIds.includes(ev.evidenceId))
    : [];

  const isAi = ev.kind === 'ERROR_RECORD' || citing.some((f) => f.claimOrigin === 'MODEL_INFERRED');

  return {
    id: ev.evidenceId,
    label: `${ev.evidenceId} · ${neutralPointer(ev.sourcePointer)} (${ev.kind})`,
    kind: ev.kind,
    sourceClass: isAi ? 'MODEL_ASSISTED' : 'RAW_TOOL_OUTPUT',
    sourcePointer: ev.sourcePointer,
    sourceSystem: sourceRecord?.sourceSystem ?? 'Traceira collector',
    toolVersion: sourceRecord?.sourceVersion ?? '1.0',
    capturedAt: ev.capturedAt,
    sha256: sourceRecord?.sha256,
    storageRef: sourceRecord?.storageRef,
    integrityState: 'UNVERIFIED',
    summary: ev.summary ?? `Observation for ${neutralPointer(ev.sourcePointer)}`,
    content: ev.summary ?? `${ev.kind} observation at ${neutralPointer(ev.sourcePointer)}`,
    isAiInterpretation: isAi,
    citingFindings: citing.map((f) => ({
      findingId: f.findingId,
      title: f.title,
      severity: f.normalizedSeverity,
    })),
  };
}

/**
 * Standard Longest Common Subsequence line diffing to produce shared line anchors.
 */
export function diffContentLines(baselineText?: string, targetText?: string): CompareAnchor[] {
  if (!baselineText && !targetText) return [];

  const bLines = baselineText ? baselineText.split(/\r?\n/) : [];
  const tLines = targetText ? targetText.split(/\r?\n/) : [];

  // If one is empty
  if (bLines.length === 0 && tLines.length > 0) {
    return tLines.map((line, idx) => ({
      id: `content-line-target-${idx + 1}`,
      label: `Content · Line ${idx + 1}`,
      category: 'content',
      status: 'ADDED',
      statusLabel: '[ADDED] Added line',
      glyph: '+',
      diffExplanation: `Line ${idx + 1} added in target version`,
      baselineValue: null,
      targetValue: line,
      isMonospace: true,
    }));
  }

  if (bLines.length > 0 && tLines.length === 0) {
    return bLines.map((line, idx) => ({
      id: `content-line-base-${idx + 1}`,
      label: `Content · Line ${idx + 1}`,
      category: 'content',
      status: 'REMOVED',
      statusLabel: '[REMOVED] Omitted line',
      glyph: '-',
      diffExplanation: `Line ${idx + 1} removed from target version`,
      baselineValue: line,
      targetValue: null,
      isMonospace: true,
    }));
  }

  // Build DP table for LCS
  const m = bLines.length;
  const n = tLines.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array<number>(n + 1).fill(0));

  const getDp = (r: number, c: number): number => {
    const row = dp[r];
    return row && row[c] !== undefined ? row[c] : 0;
  };
  const setDp = (r: number, c: number, val: number): void => {
    const row = dp[r];
    if (row) row[c] = val;
  };

  for (let i = 0; i < m; i++) {
    for (let j = 0; j < n; j++) {
      if (bLines[i] === tLines[j]) {
        setDp(i + 1, j + 1, getDp(i, j) + 1);
      } else {
        setDp(i + 1, j + 1, Math.max(getDp(i + 1, j), getDp(i, j + 1)));
      }
    }
  }

  // Backtrack to extract diff sequence
  interface DiffOp {
    type: 'same' | 'added' | 'removed' | 'modified';
    baseLine?: string;
    targetLine?: string;
  }

  const rawOps: DiffOp[] = [];
  let i = m;
  let j = n;

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && bLines[i - 1] === tLines[j - 1]) {
      rawOps.unshift({ type: 'same', baseLine: bLines[i - 1], targetLine: tLines[j - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || getDp(i, j - 1) >= getDp(i - 1, j))) {
      rawOps.unshift({ type: 'added', targetLine: tLines[j - 1] });
      j--;
    } else if (i > 0 && (j === 0 || getDp(i, j - 1) < getDp(i - 1, j))) {
      rawOps.unshift({ type: 'removed', baseLine: bLines[i - 1] });
      i--;
    }
  }

  // Pair consecutive removed + added lines into modified anchors for tighter alignment
  const pairedOps: DiffOp[] = [];
  for (let idx = 0; idx < rawOps.length; idx++) {
    const cur = rawOps[idx];
    if (!cur) continue;
    const next = rawOps[idx + 1];
    if (cur.type === 'removed' && next && next.type === 'added') {
      pairedOps.push({
        type: 'modified',
        baseLine: cur.baseLine,
        targetLine: next.targetLine,
      });
      idx++; // skip paired next
    } else {
      pairedOps.push(cur);
    }
  }

  return pairedOps.map((op, idx) => {
    const lineNum = idx + 1;
    switch (op.type) {
      case 'same':
        return {
          id: `content-line-${lineNum}`,
          label: `Content · Line ${lineNum}`,
          category: 'content',
          status: 'UNCHANGED',
          statusLabel: '[MATCH] Identical line',
          glyph: '✓',
          diffExplanation: `Line ${lineNum} matches across both versions`,
          baselineValue: op.baseLine ?? null,
          targetValue: op.targetLine ?? null,
          isMonospace: true,
        };
      case 'modified':
        return {
          id: `content-line-${lineNum}`,
          label: `Content · Line ${lineNum}`,
          category: 'content',
          status: 'MODIFIED',
          statusLabel: '[MODIFIED] Changed line',
          glyph: '≠',
          diffExplanation: `Line ${lineNum} modified from baseline to target`,
          baselineValue: op.baseLine ?? null,
          targetValue: op.targetLine ?? null,
          isMonospace: true,
        };
      case 'added':
        return {
          id: `content-line-${lineNum}`,
          label: `Content · Line ${lineNum}`,
          category: 'content',
          status: 'ADDED',
          statusLabel: '[ADDED] Added in target',
          glyph: '+',
          diffExplanation: `Line ${lineNum} added in comparison version`,
          baselineValue: null,
          targetValue: op.targetLine ?? null,
          isMonospace: true,
        };
      case 'removed':
        return {
          id: `content-line-${lineNum}`,
          label: `Content · Line ${lineNum}`,
          category: 'content',
          status: 'REMOVED',
          statusLabel: '[REMOVED] Omitted in target',
          glyph: '-',
          diffExplanation: `Line ${lineNum} present in baseline only`,
          baselineValue: op.baseLine ?? null,
          targetValue: null,
          isMonospace: true,
        };
    }
  });
}

/**
 * Builds aligned structural comparison anchors between two explicitly selected evidence versions.
 */
export function buildEvidenceComparison(
  baseline: EvidenceCompareItem | null | undefined,
  target: EvidenceCompareItem | null | undefined,
): EvidenceComparisonResult {
  if (!baseline || !target) {
    return {
      isReady: false,
      baseline: baseline ?? null,
      target: target ?? null,
      anchors: [],
      summary: {
        totalAnchors: 0,
        unchangedCount: 0,
        modifiedCount: 0,
        addedCount: 0,
        removedCount: 0,
        driftCount: 0,
        hasDifferences: false,
      },
    };
  }

  const anchors: CompareAnchor[] = [];

  // Anchor 1: Evidence Identifier
  const sameRecord = baseline.id === target.id;
  anchors.push({
    id: 'identity-id',
    label: 'Evidence identifier',
    category: 'identity',
    status: sameRecord ? 'UNCHANGED' : 'MODIFIED',
    statusLabel: sameRecord ? '[MATCH] Same record' : '[DISTINCT] Distinct records',
    glyph: sameRecord ? '✓' : '≠',
    diffExplanation: sameRecord
      ? `Both selections point to the same record: ${baseline.id}`
      : `Comparing baseline record ${baseline.id} against target record ${target.id}`,
    baselineValue: baseline.id,
    targetValue: target.id,
    isMonospace: true,
  });

  // Anchor 2: Source Pointer
  const samePointer = baseline.sourcePointer === target.sourcePointer;
  anchors.push({
    id: 'identity-pointer',
    label: 'Source pointer',
    category: 'identity',
    status: samePointer ? 'UNCHANGED' : 'MODIFIED',
    statusLabel: samePointer ? '[MATCH] Identical target' : '[MODIFIED] Changed target',
    glyph: samePointer ? '✓' : '≠',
    diffExplanation: samePointer
      ? `Both versions target the same pointer: ${neutralPointer(baseline.sourcePointer)}`
      : `Target shifted from "${neutralPointer(baseline.sourcePointer)}" in baseline to "${neutralPointer(target.sourcePointer)}" in comparison`,
    baselineValue: neutralPointer(baseline.sourcePointer),
    targetValue: neutralPointer(target.sourcePointer),
    isMonospace: true,
  });

  // Anchor 3: Evidence Kind
  const sameKind = baseline.kind === target.kind;
  anchors.push({
    id: 'classification-kind',
    label: 'Evidence kind',
    category: 'classification',
    status: sameKind ? 'UNCHANGED' : 'MODIFIED',
    statusLabel: sameKind ? '[MATCH] Identical kind' : '[MODIFIED] Kind changed',
    glyph: sameKind ? '✓' : '≠',
    diffExplanation: sameKind
      ? `Both records represent ${baseline.kind}`
      : `Evidence type changed from ${baseline.kind} in baseline to ${target.kind} in comparison`,
    baselineValue: baseline.kind,
    targetValue: target.kind,
  });

  // Anchor 4: Trust Classification / Source Class
  const sameClass = baseline.sourceClass === target.sourceClass;
  anchors.push({
    id: 'classification-source-class',
    label: 'Trust classification',
    category: 'classification',
    status: sameClass ? 'UNCHANGED' : 'DRIFT',
    statusLabel: sameClass ? '[MATCH] Same trust class' : '[DRIFT] Trust class changed',
    glyph: sameClass ? '✓' : '!',
    diffExplanation: sameClass
      ? `Both records share ${baseline.sourceClass} trust classification`
      : `Trust boundary drift: baseline is ${baseline.sourceClass} while target is ${target.sourceClass}`,
    baselineValue: baseline.sourceClass,
    targetValue: target.sourceClass,
  });

  // Anchor 5: Provenance & Collector Tool
  const baseCollector = collectorText(baseline.sourceSystem, baseline.toolVersion);
  const targetCollector = collectorText(target.sourceSystem, target.toolVersion);
  const sameCollector = baseCollector === targetCollector;
  anchors.push({
    id: 'provenance-collector',
    label: 'Collector tool & version',
    category: 'provenance',
    status: sameCollector ? 'UNCHANGED' : 'DRIFT',
    statusLabel: sameCollector ? '[MATCH] Same collector' : '[SOURCE_DRIFT] Collector drift',
    glyph: sameCollector ? '✓' : '!',
    diffExplanation: sameCollector
      ? `Both records were collected by ${baseCollector}`
      : `Collector drift: baseline observed by ${baseCollector}; comparison by ${targetCollector}`,
    baselineValue: baseCollector,
    targetValue: targetCollector,
  });

  // Anchor 6: Capture Timestamp
  const sameTimestamp = baseline.capturedAt === target.capturedAt;
  anchors.push({
    id: 'provenance-captured-at',
    label: 'Capture timestamp',
    category: 'provenance',
    status: sameTimestamp ? 'UNCHANGED' : 'MODIFIED',
    statusLabel: sameTimestamp ? '[MATCH] Same timestamp' : '[TIME_DELTA] Different capture time',
    glyph: sameTimestamp ? '✓' : '·',
    diffExplanation: sameTimestamp
      ? `Captured at the exact same moment: ${baseline.capturedAt}`
      : `Baseline captured at ${baseline.capturedAt}; comparison captured at ${target.capturedAt}`,
    baselineValue: baseline.capturedAt,
    targetValue: target.capturedAt,
    isMonospace: true,
  });

  // Anchor 7: Cryptographic Hash / Digest
  const baseHash = baseline.sha256 ? `sha256:${baseline.sha256}` : null;
  const targetHash = target.sha256 ? `sha256:${target.sha256}` : null;
  const bothHashed = Boolean(baseHash && targetHash);
  const sameHash = bothHashed ? baseHash === targetHash : !baseHash && !targetHash;
  anchors.push({
    id: 'integrity-sha256',
    label: 'Cryptographic hash (SHA-256)',
    category: 'integrity',
    status: sameHash ? 'UNCHANGED' : 'DRIFT',
    statusLabel: sameHash
      ? bothHashed
        ? '[MATCH] Bit-for-bit identical'
        : '[MATCH] No hash on either version'
      : '[INTEGRITY_DRIFT] Hash mismatch',
    glyph: sameHash ? '✓' : '!',
    diffExplanation: sameHash
      ? bothHashed
        ? 'Bit-for-bit cryptographic integrity verified: sha256 checksums match'
        : 'Neither record has a cryptographic checksum recorded'
      : `Integrity drift: checksum changed from ${baseHash ?? 'none'} to ${targetHash ?? 'none'}`,
    baselineValue: baseHash ?? 'No hash recorded',
    targetValue: targetHash ?? 'No hash recorded',
    isMonospace: true,
  });

  // Anchor 8: Observation Summary
  const sameSummary = (baseline.summary ?? '') === (target.summary ?? '');
  anchors.push({
    id: 'summary-text',
    label: 'Observation summary',
    category: 'summary',
    status: sameSummary ? 'UNCHANGED' : 'MODIFIED',
    statusLabel: sameSummary ? '[MATCH] Identical summary' : '[MODIFIED] Summary updated',
    glyph: sameSummary ? '✓' : '≠',
    diffExplanation: sameSummary
      ? 'Both versions share the exact same summary text'
      : 'Summary statement updated between baseline and comparison versions',
    baselineValue: baseline.summary ?? '—',
    targetValue: target.summary ?? '—',
  });

  // Anchor 9: Content line anchors
  const contentAnchors = diffContentLines(baseline.content, target.content);
  anchors.push(...contentAnchors);

  // Anchor 10: Citing Findings
  const baseFindingIds = (baseline.citingFindings ?? []).map((f) => f.findingId).sort();
  const targetFindingIds = (target.citingFindings ?? []).map((f) => f.findingId).sort();
  const sameCitations =
    baseFindingIds.length === targetFindingIds.length &&
    baseFindingIds.every((id, idx) => id === targetFindingIds[idx]);

  anchors.push({
    id: 'citations-findings',
    label: 'Cited by findings',
    category: 'citations',
    status: sameCitations ? 'UNCHANGED' : 'MODIFIED',
    statusLabel: sameCitations ? '[MATCH] Same citations' : '[MODIFIED] Citations changed',
    glyph: sameCitations ? '✓' : '≠',
    diffExplanation: sameCitations
      ? `Both records cited by ${baseFindingIds.length === 0 ? 'no findings' : baseFindingIds.join(', ')}`
      : `Citations changed: baseline cited by [${baseFindingIds.join(', ') || 'none'}]; comparison cited by [${targetFindingIds.join(', ') || 'none'}]`,
    baselineValue: baseFindingIds.join(', ') || 'None',
    targetValue: targetFindingIds.join(', ') || 'None',
    isMonospace: true,
  });

  // Summary counts
  let unchangedCount = 0;
  let modifiedCount = 0;
  let addedCount = 0;
  let removedCount = 0;
  let driftCount = 0;

  for (const a of anchors) {
    if (a.status === 'UNCHANGED') unchangedCount++;
    else if (a.status === 'MODIFIED') modifiedCount++;
    else if (a.status === 'ADDED') addedCount++;
    else if (a.status === 'REMOVED') removedCount++;
    else if (a.status === 'DRIFT') driftCount++;
  }

  const hasDifferences = modifiedCount > 0 || addedCount > 0 || removedCount > 0 || driftCount > 0;

  return {
    isReady: true,
    baseline,
    target,
    anchors,
    summary: {
      totalAnchors: anchors.length,
      unchangedCount,
      modifiedCount,
      addedCount,
      removedCount,
      driftCount,
      hasDifferences,
    },
  };
}
